import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { useTelegramBackButton } from '@/utils/BackButton';
import { TimeIcon } from '@/assets/Time';
import { RestaurantIcon } from '@/assets/Restaurant';
import { DeliveryIcon } from '@/assets/Delivery';
import { CheckIcon } from '@/assets/Check';
import { LocationFooterIcon } from '@/assets/footer/Location';
import { formatPrice } from '@/utils/getPrice';
// import { ReceiptIcon } from '@/assets/Receipt';

import { useNavigate, useParams } from 'react-router-dom';
import { useGetOrderById } from '../services/getOrderById';
import {
  ORDER_PROGRESS_STEPS,
  getOrderProgressStep,
  isOrderHandedOver,
  getOrderStatusTitle,
  isAwaitingPayment,
  isOrderCancelled,
} from '../libs/orderStatus';
import { openExternalLink } from '../../../utils/telegramLink';
import { Spinner } from '../../../components/Spinner';
import { truncate } from '../../../utils/truncate';
import { formatDate } from 'date-fns';
import { ru, uz } from 'date-fns/locale';
import { cn } from '../../../lib/utils';
import RateOrderDialog from './RateDrawer';
import { CancelOrderDrawer } from './CancelOrderDrawer';
import { useState, useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import { ReceiptIcon } from '../../../assets/Receipt';
import { StarRatingInput } from './StarRatingInput';
import { ProductImage } from '../../../components/ProductImage';

const discountLabel = '\u0421\u043a\u0438\u0434\u043a\u0430';
const promocodeLabel = '\u041f\u0440\u043e\u043c\u043e\u043a\u043e\u0434';

export const OrderDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useTelegramBackButton('/profile/orders');
  const { orderId } = useParams();
  const lang = localStorage.getItem('language') || 'uz';

  const { order: orderItem, isLoading } = useGetOrderById(orderId || '');
  //const { order: orderItem, isLoading } = useGetOrderById('116816633');
  const {
    order,
    positions,
    address_title,
    review,
    fast_delivery,
    card_id,
    bonus_points_spent_amount,
    is_promocode_can_be_used,
    promocode_name,
    discount_amount,
    total_price,
    delivery_price,
    can_cancel,
    payment_url,
  } = orderItem;

  // discount_amount — grand_total минус итог, туда попадают и списанные
  // E-coins. Они уже показаны своей строкой, поэтому в «Скидке» — только
  // остаток (промокод/акции); если заказ оплачен одними E-coins, её нет.
  const promoDiscount = Math.max(discount_amount - bonus_points_spent_amount, 0);

  const [isOpen, setIsOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  // Отмена зависит от статусов заказа и платежа, а их нумерацию бэкенд нигде
  // не описывает — печатаем на стенде, чтобы не гадать. В прод не попадает.
  if (import.meta.env.DEV && order.id) {
    console.info('[order]', {
      id: order.id,
      last_status_id: order.last_status_id,
      last_status_title: order.last_status_title,
      last_payment_status_id: order.last_payment_status_id,
      last_payment_status_title: order.last_payment_status_title,
      last_payment_title: order.last_payment_title,
      can_cancel,
    });
  }
  const [timeLeft, setTimeLeft] = useState('');
  const [isOrderLate, setIsOrderLate] = useState(false);

  useEffect(() => {
    if (!fast_delivery || !order.order_accepted_on || !order.delivery_on)
      return;

    const updateTimer = () => {
      const now = new Date();
      const deliveryTime = new Date(order.delivery_on);
      const timeDiff = deliveryTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeLeft('00:00');
        setIsOrderLate(true);
        return;
      }

      const minutes = Math.floor(timeDiff / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
      setTimeLeft(
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [fast_delivery, order.order_accepted_on, order.delivery_on]);

  if (isLoading) {
    return <Spinner />;
  }
  const isCancelled = isOrderCancelled(order.last_status_id);
  const awaitingPayment = isAwaitingPayment(order);
  const progressStep = getOrderProgressStep(order.last_status_id);
  // Оценка — только когда заказ уже у клиента (см. isOrderHandedOver).
  const isHandedOver = isOrderHandedOver(order.last_status_id);
  // Иконки трекера по макету: принят → готовится → курьер → доставлен.
  const trackerSteps = [
    { Icon: TimeIcon, iconClassName: '' },
    { Icon: RestaurantIcon, iconClassName: '' },
    { Icon: DeliveryIcon, iconClassName: 'size-6' },
    { Icon: CheckIcon, iconClassName: 'size-4' },
  ];
  // Линия тянется от первого кружка до текущего шага; 90% — вся дорожка.
  const progressLineWidth = `${((progressStep - 1) / (ORDER_PROGRESS_STEPS - 1)) * 90}%`;

  return (
    <div className="flex h-full flex-col overflow-y-auto pt-4">
      <div className="flex items-center flex-row justify-between  px-4">
        <div />
        <div className="flex items-center flex-col pl-8 ">
          <h1 className="text-display-sm text-center ">
            {t('orderDetailsTitlePrefix')}
            {order.id}
          </h1>

          <p className="text-body-sm-regular text-text-secondary">{`${formatDate(order.created_on, 'dd MMMM, HH:mm', { locale: lang === 'ru' ? ru : uz })}`}</p>
        </div>
        <div onClick={() => navigate(`/profile/orders/${order.id}/receipt`)}>
          <ReceiptIcon />
        </div>
      </div>

      <div className="flex-grow p-4 space-y-4">
        <Card className="rounded-2xl">
          <CardHeader className="p-4 text-center space-y-0">
            <h2 className="text-display-md mb-2">
              {getOrderStatusTitle(t, order)}
            </h2>
            {order.last_status_id !== 5 && !isCancelled && !awaitingPayment ? (
              <p className="text-body-sm-regular text-text-secondary">
                {t('estimated_delivery_time')} <br />~
                {formatDate(order.delivery_on, 'HH:mm ', {
                  locale: lang === 'ru' ? ru : uz,
                })}
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Отменённый заказ: вместо трекера — «X» под заголовком статуса.
                Отдельно «X» и по отменённому платежу — так было до этого. */}
            {isCancelled || order.last_payment_status_id === 2 ? (
              <div className="flex items-center justify-center">
                <div className="h-10 w-10 bg-fill-danger rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-fill-background" />
                </div>
              </div>
            ) : null}

            {/* Ожидание оплаты: заказ ещё не в работе, трекер показывать рано —
                вместо него часы и кнопка на страницу оплаты. */}
            {awaitingPayment ? (
              <div className="flex flex-col items-center gap-4">
                <div className="h-10 w-10 bg-[#FFF4E0] rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#FF9900]" />
                </div>
                {payment_url && (
                  <Button
                    onClick={() => openExternalLink(payment_url)}
                    className="bg-button-primary h-12 rounded-xl w-full"
                  >
                    <p className="text-display-sm font-semibold">{t('pay')}</p>
                  </Button>
                )}
              </div>
            ) : null}

            {!isCancelled && !awaitingPayment && (
              <div className="flex justify-between items-center relative my-2 px-2">
                <div className="absolute w-[90%] h-0.5 bg-gray-200 top-1/2" />
                <div
                  className="absolute h-0.5 bg-brand-green top-1/2 z-10"
                  style={{ width: progressLineWidth }}
                />
                {trackerSteps.map(({ Icon, iconClassName }, index) => {
                  const active = index < progressStep;

                  return (
                    <div
                      key={index}
                      className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center z-20',
                        active ? 'bg-primitive-green' : 'bg-fill-background'
                      )}
                    >
                      <Icon
                        className={cn(
                          iconClassName,
                          active ? 'text-brand-green' : 'text-text-icon-tertiary'
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex flex-col space-y-2 items-center">
              {isHandedOver && review ? (
                <>
                  <StarRatingInput
                    value={review.order_mark}
                    onChange={() => {}}
                    readOnly={true}
                  />
                  {review.user_comment && <p>{review.user_comment}</p>}
                </>
              ) : (
                <>
                  {isHandedOver && !review && (
                    <Button
                      onClick={() => setIsOpen(true)}
                      className="bg-button-primary h-12 rounded-xl"
                    >
                      <p className="text-display-sm font-semibold">
                        {t('rateOrderButton')}
                      </p>
                    </Button>
                  )}
                </>
              )}

              {/* Обещание «привезём за N минут» — пока заказ едет. После
                  выдачи и на отменённом оно уже ни о чём. */}
              {fast_delivery && !isHandedOver && !isCancelled && (
                  <div className="flex items-center justify-center px-4 py-2 rounded-lg">
                    {isOrderLate ? (
                      <div className="flex flex-col items-center text-center">
                        <div className="px-3 py-1 rounded-lg mb-1">
                          <span className="font-semibold">
                            {card_id ? t('orderLateCard') : t('orderLateCash')}
                          </span>
                        </div>
                        <div className="px-3 py-1 rounded-lg">
                          <span className="text-sm font-medium">
                            {card_id
                              ? t('orderRefundCard')
                              : t('orderFreeCash')}
                          </span>
                        </div>
                      </div>
                    ) : timeLeft ? (
                      <>
                        <TimeIcon className="w-4 h-4 mr-2" />
                        <span className="font-semibold">{timeLeft}</span>
                      </>
                    ) : null}
                  </div>
                )}

              {/* По макету — не кнопка, а текстовая ссылка под действиями:
                  отмена не должна спорить за внимание с основным действием. */}
              {can_cancel && (
                <button
                  type="button"
                  onClick={() => setIsCancelOpen(true)}
                  className="pt-1.5 text-body-sm-medium text-text-secondary"
                >
                  {t('cancel-order')}
                </button>
              )}

              {/* <Button
                variant="ghost"
                className="flex-1 text-text-secondary"
                onClick={handleRateOrder}
              >
                {t('writeToSupportButton')}
              </Button> */}
            </div>
          </CardContent>
        </Card>

        <CancelOrderDrawer
          orderId={order.id}
          open={isCancelOpen}
          setOpen={setIsCancelOpen}
        />

        <Accordion
          type="single"
          collapsible
          defaultValue="item-1"
          className="w-full"
        >
          <AccordionItem value="item-1" className="border-none">
            <Card className="rounded-2xl shadow-sm">
              <AccordionTrigger className="px-4 py-3">
                {t('orderDetailsSectionTitle')}
              </AccordionTrigger>
            </Card>

            <AccordionContent className="p-0 pt-2 space-y-2">
              <Card className="p-4">
                <p className="text-body-sm-medium text-text-secondary">
                  {t('deliveryDetailsLabel')}
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <div className="h-9 w-9 rounded-[10px] flex items-center justify-center bg-gray-100">
                    <LocationFooterIcon className="size-5 text-text-icon-secondary" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-600">{address_title}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 pt-2 pr-0">
                <p className="text-body-sm-medium text-text-secondary mb-2">
                  {t('inOrderLabel')}
                </p>
                <div className="space-y-3 py-3">
                  {positions.map(item => (
                    <div key={item.id} className="flex items-center space-x-3">
                      <ProductImage
                        src={item.image}
                        alt={item.title}
                        className="w-[58px] h-[58px] rounded-xl border border-secondary object-contain p-1"
                      />
                      <div className="flex items-center justify-between border-b border-secondary w-full h-[58px]">
                        <div>
                          <p className="text-body-base-regular">
                            {truncate(item.title, 15)}
                          </p>
                          <p className="text-body-sm-regular text-text-tertiary">
                            x{item.count}
                          </p>
                        </div>
                        <div className="flex flex-col items-end mr-4">
                          {/* {item.oldPrice && (
                            <p className="relative inline-block text-caption-sm-medium text-text-tertiary">
                              {formatPrice(item.oldPrice)} {order.currency}
                              <span className="absolute left-0 top-2 w-full h-[1px] bg-fill-danger rotate-[4deg] origin-top-right" />
                            </p>
                          )} */}
                          <p className="text-body-base-regular">
                            {formatPrice(Number(item.price))} {t('sum')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-1 text-sm ml-1 mr-4">
                  {/* {order.discount && (
                    <div className="flex justify-between space-y-1.5">
                      <span className="text-body-base-regular text-text-tertiary">
                        {t('discountLabel')}
                      </span>
                      <span className="text-body-base-regular text-button-primary">
                        -{formatPrice(order.discount)} {order.currency}
                      </span>
                    </div>
                  )} */}
                  <div className="flex justify-between space-y-1.5">
                    <span className="text-body-base-regular text-text-tertiary">
                      {t('deliveryLabel')}
                    </span>
                    <span className="text-body-sm-regular">
                      {formatPrice(delivery_price)} {t('sum')}
                    </span>
                  </div>
                  {bonus_points_spent_amount > 0 ? (
                    <div className="flex justify-between space-y-1.5">
                      <span className="text-body-base-regular text-text-tertiary">
                        {t('points-payment')}
                      </span>
                      <span className="text-body-sm-regular">
                        {formatPrice(bonus_points_spent_amount)} {t('sum')}
                      </span>
                    </div>
                  ) : null}
                  {is_promocode_can_be_used ? (
                    <div className="flex justify-between space-y-1.5">
                      <span className="text-body-base-regular text-text-tertiary">
                        {promocodeLabel}
                      </span>
                      <span className="text-body-sm-regular">
                        {promocode_name}
                      </span>
                    </div>
                  ) : null}
                  {promoDiscount > 0 ? (
                    <div className="flex justify-between space-y-1.5">
                      <span className="text-body-base-regular text-text-tertiary">
                        {discountLabel}
                      </span>
                      <span className="text-body-sm-regular">
                        -{formatPrice(promoDiscount)} {t('sum')}
                      </span>
                    </div>
                  ) : null}
                  
                  <Separator className="my-8 h-[1px]" />
                  <div className="flex justify-between font-semibold space-y-2">
                    <span className="text-body-base-regular text-text-tertiary">
                      {t('totalLabel')}
                    </span>
                    <span className="text-body-base-semibold">
                      {formatPrice(total_price)} {t('sum')}
                    </span>
                  </div>
                </div>
              </Card>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* <Card className="rounded-2xl">
          <CardHeader className="p-3">
            <CardTitle className="text-body-base-semibold text-text-secondary">
              {t('paymentMethodLabel')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center space-x-4">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <span className="text-body-sm-medium">
                {order.maskedCardNumber}
              </span>
            </div>
          </CardContent>
        </Card> */}

        {/* <Card className="rounded-2xl">
          <CardHeader className="p-4 pt-3">
            <CardTitle className="text-body-sm-medium text-text-secondary">
              {t('deliveryMethodLabel')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="text-body-sm-medium bg-gray-100 px-2.5 py-1.5 rounded-full w-fit">
              {order.comment}
            </div>
          </CardContent>
        </Card> */}
      </div>

      <RateOrderDialog
        orderNumber={order.id.toString()}
        open={isOpen}
        setOpen={setIsOpen}
        order={order.id}
      />
    </div>
  );
};

export default OrderDetails;
